import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";

/**
 * Blink fade for comfortable teleport locomotion. A black quad parented to the
 * active camera (so it covers both eyes in XR) ramps opacity:
 *   fade-out (fast) → hold at full black → [reposition happens here] → fade-in.
 * The reposition callback fires ONLY at full opacity, so the camera never
 * visibly jumps. Fade-out is faster than fade-in, per the comfort spec.
 */
const FADE_OUT_MS = 150;
const HOLD_MS = 70;
const FADE_IN_MS = 200;

type Phase = "idle" | "out" | "hold" | "in";

export class BlinkFade {
  private readonly plane: Mesh;
  private readonly mat: StandardMaterial;
  private phase: Phase = "idle";
  private elapsed = 0;
  private onOpaque: (() => void) | null = null;
  private resolve: (() => void) | null = null;

  constructor(scene: Scene) {
    this.plane = CreatePlane("blinkFade", { size: 4 }, scene);
    this.mat = new StandardMaterial("blinkFadeMat", scene);
    this.mat.diffuseColor = new Color3(0, 0, 0);
    this.mat.emissiveColor = new Color3(0, 0, 0);
    this.mat.disableLighting = true;
    this.mat.disableDepthWrite = true;
    this.mat.alpha = 0;
    this.plane.material = this.mat;
    this.plane.isPickable = false;
    this.plane.renderingGroupId = 3; // draw over everything
    this.plane.setEnabled(false);

    scene.onBeforeRenderObservable.add(() => {
      this.update(scene.getEngine().getDeltaTime());
    });
  }

  get isFading(): boolean {
    return this.phase !== "idle";
  }

  /** Run one blink. `onOpaque` fires at full black; the promise resolves when clear again. */
  blink(scene: Scene, onOpaque: () => void): Promise<void> {
    if (this.phase !== "idle") return Promise.resolve();
    this.onOpaque = onOpaque;
    this.phase = "out";
    this.elapsed = 0;
    // Cover whichever camera is currently rendering.
    this.plane.parent = scene.activeCamera;
    this.plane.position.set(0, 0, 0.4);
    this.plane.rotation.set(0, 0, 0);
    this.plane.setEnabled(true);
    return new Promise((res) => (this.resolve = res));
  }

  private update(dtMs: number): void {
    if (this.phase === "idle") return;
    this.elapsed += dtMs;
    switch (this.phase) {
      case "out":
        this.mat.alpha = Math.min(1, this.elapsed / FADE_OUT_MS);
        if (this.elapsed >= FADE_OUT_MS) {
          this.mat.alpha = 1;
          this.onOpaque?.();
          this.onOpaque = null;
          this.phase = "hold";
          this.elapsed = 0;
        }
        break;
      case "hold":
        if (this.elapsed >= HOLD_MS) {
          this.phase = "in";
          this.elapsed = 0;
        }
        break;
      case "in":
        this.mat.alpha = Math.max(0, 1 - this.elapsed / FADE_IN_MS);
        if (this.elapsed >= FADE_IN_MS) {
          this.mat.alpha = 0;
          this.plane.setEnabled(false);
          this.phase = "idle";
          this.resolve?.();
          this.resolve = null;
        }
        break;
    }
  }
}
